import React from "react";
import LoadingSpinner from "../loadingSpinner";
import { wsTrans } from "../../utils/utils";
import { readXBoards, saveJPros } from "../C18AiUtils";
import { cl, globs } from "../../../components/utils/utils";

const TEXT = "#2c3e50";
const TEXT_MUTED = "#8a9aac";
const BORDER = "#c5ced8";
const SELECTED_BG = "#80A0C0";

const pageWrap = {
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  padding: 20,
  width: "100%",
  maxWidth: 720,
  boxSizing: "border-box",
};

const titleTyp = {
  fontSize: 22,
  fontWeight: 700,
  color: TEXT,
  margin: "0 0 6px 0",
};

const subTyp = {
  fontSize: 13,
  color: TEXT_MUTED,
  marginBottom: 18,
  lineHeight: 1.4,
};

const sectionTitle = {
  fontSize: 16,
  fontWeight: 700,
  color: TEXT,
  margin: "20px 0 4px 0",
};

const sectionSub = {
  fontSize: 12,
  color: TEXT_MUTED,
  marginBottom: 12,
};

const unitTable = {
  borderCollapse: "collapse",
  width: "auto",
  maxWidth: "100%",
  marginBottom: 8,
  fontSize: 14,
};

const unitTh = {
  textAlign: "left",
  padding: "6px 12px",
  borderBottom: `1px solid ${BORDER}`,
  color: TEXT_MUTED,
  fontSize: 12,
  fontWeight: 600,
};

const unitTd = {
  padding: "8px 12px",
  borderBottom: `1px solid ${BORDER}`,
  color: TEXT,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const pumperCard = {
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  padding: 10,
  marginBottom: 10,
  background: "#fff",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const pumperIdTyp = {
  width: 24,
  flexShrink: 0,
  fontSize: 14,
  fontWeight: 600,
  color: TEXT_MUTED,
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
};

const pumperNameInput = {
  height: 36,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${BORDER}`,
  boxSizing: "border-box",
  fontSize: 14,
  fontFamily: "inherit",
  color: TEXT,
  background: "#fff",
  flex: 1,
  minWidth: 0,
  width: "auto",
};

const addBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginTop: 4,
  padding: "10px 14px",
  borderRadius: 8,
  border: `1px dashed ${BORDER}`,
  background: "#fafbfc",
  color: TEXT_MUTED,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

function blankNutes() {
  return [
    "None", "None", "None", "None", "None", "None", "None", "None",
    "None", "None", "None", "None", "None", "None", "None", "None",
  ];
}

function blankBoard(index) {
  return {
    name: `jPlus ${index + 1}`,
    mbAddr: 1,
    nutes: blankNutes(),
    cps: [1, 2, 3, 4, 5, 6, 7, 8, 1, 2, 3, 4, 5, 6, 7, 8],
  };
}

function isConfiguredName(name) {
  return name != null && String(name).trim() !== "" && String(name) !== "None";
}

export default class JProSetup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      jPlus: [],
      xBoards: [],
      zInd: null,
      selectedJpIndex: 0,
      draftKey: null, // `${jpIndex}:${slot}` for newly added empty pumper
      dirty: false,
    };
    this.subscribe_savePageEvent = globs.events.subscribe(
      "savePageEvent",
      this.savePage
    );
  }

  componentDidMount() {
    this.loadInfo();
  }

  componentWillUnmount() {
    if (this.subscribe_savePageEvent && this.subscribe_savePageEvent.remove) {
      this.subscribe_savePageEvent.remove();
    }
  }

  loadInfo = async () => {
    const pa = this.props.parms;
    const zone = globs.zonesInfo.info.find((z) => z.zoneId === pa.zone);
    const res = await wsTrans("usa", {
      cmd: "cRest",
      uri: "/s/pearlDoser",
      method: "retrieve",
      sessionId: globs.userData.session.sessionId,
      body: { zoneId: pa.zone },
    });
    const stateVals = {
      loaded: true,
      xBoards: readXBoards(zone.siteZoneIndex),
      zInd: zone.siteZoneIndex,
    };
    for (const val of res.data) {
      stateVals[val.type] = val.value;
    }
    let jPlus = stateVals.jPlus || [];
    if (!jPlus.length) {
      jPlus = [blankBoard(0)];
    }
    const firstUsed = jPlus.findIndex((jp) => jp && !jp.notUsed);
    this.setState({
      loaded: true,
      jPlus,
      xBoards: stateVals.xBoards,
      zInd: stateVals.zInd,
      selectedJpIndex: firstUsed >= 0 ? firstUsed : 0,
    });
  };

  markDirty = () => {
    globs.events.publish("savePageEnable", true);
    if (this.props.onChange) {
      this.props.onChange({ type: "change", value: this.savePage });
    }
    this.setState({ dirty: true });
  };

  doSaveField = async (field, value) => {
    cl(`save ${field}`)
    cl(value)
    const pa = this.props.parms;
    await wsTrans("usa", {
      cmd: "cRest",
      uri: "/s/pearlDoser",
      method: "update",
      sessionId: globs.userData.session.sessionId,
      body: {
        zoneId: pa.zone,
        type: field,
        value,
      },
    });
  };

  savePage = async () => {
    cl("savePage")
    const { dirty, jPlus, zInd, xBoards } = this.state;
    globs.events.publish("savePageEnable", false);
    if (dirty) {
      // Clear empty draft names back to None before save
      const cleaned = jPlus.map((jp) => {
        const nutes = (jp.nutes || blankNutes()).map((n) =>
          isConfiguredName(n) ? n : "None"
        );
        return { ...jp, nutes };
      });
      await this.doSaveField("jPlus", cleaned);
//       const used = cleaned.filter((jp) => !jp.notUsed);
//       const nextX = await saveJPros(zInd, used, xBoards);
      this.setState({
        jPlus: cleaned,
//         xBoards: nextX ? nextX.slice(0) : xBoards,
        dirty: false,
        draftKey: null,
      });
      if (this.props.onChange) {
        this.props.onChange({ type: "change", value: null });
      }
    }
    globs.events.publish("saveOK", true);
  };

  listActiveUnits = () => {
    const out = [];
    (this.state.jPlus || []).forEach((jp, index) => {
      if (jp && !jp.notUsed) {
        out.push({ jp, index });
      }
    });
    return out;
  };

  selectUnit = (index) => {
    if (index === this.state.selectedJpIndex) return;
    this.setState({ selectedJpIndex: index });
  };

  getSelectedJpIndex = () => {
    const { jPlus, selectedJpIndex } = this.state;
    if (jPlus[selectedJpIndex] && !jPlus[selectedJpIndex].notUsed) {
      return selectedJpIndex;
    }
    const first = (jPlus || []).findIndex((jp) => jp && !jp.notUsed);
    return first >= 0 ? first : -1;
  };

  listPumpers = () => {
    const { jPlus, draftKey } = this.state;
    const selectedJpIndex = this.getSelectedJpIndex();
    if (selectedJpIndex < 0) return [];
    const nutes = jPlus[selectedJpIndex].nutes || [];

    // Show through the last configured slot, or a trailing draft from Add pumper.
    // Blank holes before that stay visible; trailing blanks beyond it do not.
    let lastShow = -1;
    nutes.forEach((name, slot) => {
      const key = `${selectedJpIndex}:${slot}`;
      if (isConfiguredName(name) || draftKey === key) {
        lastShow = slot;
      }
    });

    const out = [];
    for (let slot = 0; slot <= lastShow; slot++) {
      const name = nutes[slot];
      out.push({
        key: `${selectedJpIndex}:${slot}`,
        jpIndex: selectedJpIndex,
        slot,
        name: name === "None" || name == null ? "" : name,
      });
    }
    return out;
  };

  setPumperName = (jpIndex, slot, value) => {
    const jPlus = this.state.jPlus.slice(0);
    if (!jPlus[jpIndex]) return;
    const nutes = (jPlus[jpIndex].nutes || blankNutes()).slice(0);
    nutes[slot] = value;
    jPlus[jpIndex] = { ...jPlus[jpIndex], nutes };
    const key = `${jpIndex}:${slot}`;
    const draftKey =
      this.state.draftKey === key && isConfiguredName(value)
        ? null
        : this.state.draftKey;
    this.setState({ jPlus, draftKey });
    this.markDirty();
  };

  addPumper = () => {
    const { jPlus, draftKey } = this.state;
    if (draftKey) return;

    const next = jPlus.slice(0);
    const jpIndex = this.getSelectedJpIndex();
    if (jpIndex < 0 || !next[jpIndex]) return;

    const nutes = (next[jpIndex].nutes || blankNutes()).slice(0);
    let lastConfigured = -1;
    nutes.forEach((n, i) => {
      if (isConfiguredName(n)) lastConfigured = i;
    });
    const slot = lastConfigured + 1;
    if (slot >= nutes.length) return;

    nutes[slot] = "";
    next[jpIndex] = { ...next[jpIndex], nutes };

    this.setState({
      jPlus: next,
      selectedJpIndex: jpIndex,
      draftKey: `${jpIndex}:${slot}`,
    });
    this.markDirty();
  };

  render() {
    const { loaded, jPlus } = this.state;
    if (!loaded) {
      return <LoadingSpinner message="loading JPro setup..." />;
    }

    const units = this.listActiveUnits();
    const selectedJpIndex = this.getSelectedJpIndex();
    const selected = selectedJpIndex >= 0 ? jPlus[selectedJpIndex] : null;
    const selectedName =
      selected && selected.name != null && String(selected.name).trim() !== ""
        ? selected.name
        : "JPro";
    const pumpers = this.listPumpers();

    return (
      <div style={pageWrap}>
        <h2 style={titleTyp}>JPro Setup</h2>
        <div style={subTyp}>
          Registers per the HE Anderson J Pro Modbus map. Configured nutrients
          become the recipe channels.
        </div>

        <h3 style={sectionTitle}>JPro units</h3>
        <div style={sectionSub}>select a unit to edit its pumpers</div>
        {units.length === 0 ? (
          <div style={{ ...sectionSub, marginBottom: 16 }}>
            No JPro units configured. Add one in System Settings.
          </div>
        ) : (
          <table style={unitTable}>
            <thead>
              <tr>
                <th style={unitTh}>#</th>
                <th style={unitTh}>Name</th>
              </tr>
            </thead>
            <tbody>
              {units.map(({ jp, index }, displayIdx) => {
                const selectedRow = index === selectedJpIndex;
                return (
                  <tr
                    key={index}
                    onClick={() => this.selectUnit(index)}
                    style={{
                      background: selectedRow ? SELECTED_BG : "transparent",
                    }}
                  >
                    <td
                      style={{
                        ...unitTd,
                        color: selectedRow ? "#fff" : TEXT,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {displayIdx + 1}
                    </td>
                    <td
                      style={{
                        ...unitTd,
                        color: selectedRow ? "#fff" : TEXT,
                        fontWeight: selectedRow ? 600 : 400,
                      }}
                    >
                      {jp.name == null || jp.name === ""
                        ? `JPro ${displayIdx + 1}`
                        : jp.name}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {selected && (
          <>
            <h3 style={sectionTitle}>{`${selectedName} Pumpers`}</h3>
            <div style={sectionSub}>one per J Pro output</div>

            {pumpers.map((p, i) => (
              <div key={p.key} style={pumperCard}>
                <div style={pumperIdTyp} aria-label={`Pumper ${i + 1}`}>
                  {i + 1}
                </div>
                <input
                  type="text"
                  style={pumperNameInput}
                  value={p.name}
                  placeholder="Pumper name"
                  onChange={(e) =>
                    this.setPumperName(p.jpIndex, p.slot, e.target.value)
                  }
                  aria-label={`Pumper ${i + 1} name`}
                />
              </div>
            ))}

            <button type="button" style={addBtn} onClick={this.addPumper}>
              + Add pumper
            </button>
          </>
        )}
      </div>
    );
  }
}
